import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import Flag from "react-world-flags";
import { LANGUAGES } from "../constants/language";
import {
  getCurrencyFromLocalStorage,
  setCurrencyInLocalStorage,
  getI18nLanguageFromLocalStorage,
} from "../helpers/localStorageHelper";
import { modifyMediaUrl } from "../helpers/modifyMediaUrl";
import { convertUTCDateTimeInMessage } from "../helpers/convertUTCDateTimeInMessage";
import { i18n, initializeI18n } from "../localization/i18n";
import {
  changeLanguageByUser,
  translate,
} from "../localization/translateService";
import { AuthenticationService } from "../services/authService";
import { postChangeCurrency } from "../services/userService";
import { getUserEnterprises } from "../services/enterpriseService";
import {
  initializeConnection,
  getHubConnection,
  getAllNotifications,
  onNotificationsUpdated,
  updateNotification,
  initializeShoppingCartConnection,
  getShoppingCart,
  getShoppingCartHubConnection,
  onShoppingCartUpdated,
} from "../services/signalRService";
import { NOTIFICATION_STATUSES } from "../constants/notification-statuses";
import { USER_ROLES } from "../constants/user-roles";
import MainNavigation from "./common/MainNavigation";
import { getLanguageOptions } from "../services/languageService";
import { getCurrencyOptions } from "../services/currencyService";
import { useGnbScheme } from "./common/useGnbScheme";
import LanguageCurrencyMenu from "./common/LanguageCurrencyMenu";
import AccountMenu from "./common/AccountMenu";
import NotificationMenu from "./common/NotificationMenu";

const AdvertiseModal = React.lazy(() => import("./AdvertiseModal"));

const COURSE_ID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

// Notification title/body may contain HTML (e.g. <br/>) + UTC datetimes + encoded
// entities. Match the legacy portal NotificationBox/List behavior: convert UTC -> local,
// then strip tags to plain text (so <br/> etc. don't render as raw text in the GNB menu).
const stripHtmlTags = (input) => {
  if (!input) return "";
  const processed = convertUTCDateTimeInMessage(input);
  const doc = new DOMParser().parseFromString(processed, "text/html");
  return doc.body.textContent || "";
};

// ── Module-scope custom hook ─────────────────────────────────────────────────
function useClickOutside(ref, open, setOpen) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);
}

export default function Header({ props }) {
  const {
    portalUrls,
    defaultAccountAvatar,
  } = props;

  // ── refs ──────────────────────────────────────────────────────────────────
  const headerRef = useRef(null);
  const langPopoverRef = useRef(null);      // click-outside for lang trigger (non-login)
  const loginLangPopoverRef = useRef(null); // click-outside for lang trigger (login)
  const notifPopoverRef = useRef(null);
  const accountPopoverRef = useRef(null);

  // ── scheme ─────────────────────────────────────────────────────────────────
  const scheme = useGnbScheme(headerRef);

  // ── auth state ─────────────────────────────────────────────────────────────
  const currentUser$ = AuthenticationService.instance().getCurrentUser$();
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(!!currentUser);
  const [updateProfileRequired, setUpdateProfileRequired] = useState(false);
  const [hasActiveEnterprise, setHasActiveEnterprise] = useState(false);

  const isAdmin     = useMemo(() => !!currentUser?.roles?.includes("Admin"),      [currentUser?.roles]);
  const isTrainer   = useMemo(() => !!currentUser?.roles?.includes("Trainer"),    [currentUser?.roles]);
  const isStudent   = useMemo(() => !!currentUser?.roles?.includes("Student"),    [currentUser?.roles]);
  const isEnterprise = useMemo(() => !!currentUser?.roles?.includes("Enterprise"), [currentUser?.roles]);

  // ── language / currency state ───────────────────────────────────────────────
  const languageCurrentValueRef = useRef(null);
  const currencyCurrentValueRef = useRef(null);
  const [selectedLanguage, setSelectedLanguage]   = useState("");
  const [selectedCurrency, setSelectedCurrency]   = useState("");
  const [languageOptions,  setLanguageOptions]     = useState([]);
  const [currencyOptions,  setCurrencyOptions]     = useState([]);
  const [translations,     setTranslations]        = useState({});
  const [accountOptions,   setAccountOptions]      = useState([]);

  // ── popover open/close state ────────────────────────────────────────────────
  const [langMenuOpen,        setLangMenuOpen]        = useState(false); // non-login
  const [loginLangMenuOpen,   setLoginLangMenuOpen]   = useState(false); // login
  const [notifMenuOpen,       setNotifMenuOpen]       = useState(false);
  const [accountMenuOpen,     setAccountMenuOpen]     = useState(false);
  const [profileDrawerOpen,   setProfileDrawerOpen]   = useState(false);

  // ── notifications ───────────────────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.isRead).length,
    [notifications]
  );

  // ── shopping cart ─────────────────────────────────────────────────────────────
  const [cartCount, setCartCount] = useState(0);

  // ── derived helpers ─────────────────────────────────────────────────────────
  const parsePathSegments = () =>
    window.location.pathname.split("/").filter(Boolean);

  const buildLanguagePath = (lang) => {
    if (lang === "en") return "/en";
    if (lang === "vi") return "/vi";
    if (lang === "ko") return "/ko";
    return "/en";
  };

  const languagePath = buildLanguagePath(selectedLanguage);

  // Click a GNB notification -> mark it read (same hub call as the portal
  // NotificationBox.handleNavigate) then redirect: portal path by receiver when
  // `pathUrl` is set, else the notification detail page on this site.
  const handleNotificationClick = async (n) => {
    if (!n) return;
    try {
      await updateNotification({ id: n.id, status: NOTIFICATION_STATUSES.READ });
    } catch { /* mark-read best effort; still navigate */ }
    setNotifications((prev) =>
      prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x))
    );
    setNotifMenuOpen(false);
    if (n.pathUrl && n.pathUrl.trim() !== "") {
      const base =
        n.receiver === USER_ROLES.ADMIN   ? process.env.ADMIN_PORTAL_ENDPOINT :
        n.receiver === USER_ROLES.TRAINER ? process.env.TRAINER_PORTAL_ENDPOINT :
        n.receiver === USER_ROLES.STUDENT ? process.env.STUDENT_PORTAL_ENDPOINT : "";
      window.location.href = `${base}${n.pathUrl}`;
    } else {
      window.location.href = `${languagePath}/notifications/${n.id}`;
    }
  };

  const getCurrencyShortTitle = (value) =>
    currencyOptions.find((item) => item.value === value)?.shortName || "";

  const getLanguageTitle = (value) =>
    languageOptions.find((item) => item.value === value)?.name || "";

  const getLanguageCode = (value) =>
    languageOptions.find((item) => item.value === value)?.code || "US";

  const profileUrl = portalUrls?.studentProfile || "/portals/student/profile";

  const roleLabel = useMemo(() => {
    if (isAdmin)      return "Admin";
    if (isTrainer)    return "Trainer";
    if (isEnterprise) return "Enterprise";
    if (isStudent)    return "Student";
    return "";
  }, [isAdmin, isTrainer, isEnterprise, isStudent]);

  const courseButton = useMemo(() => {
    const config = props?.courseButton;
    const label = (config?.label || "").trim();
    const courseId = String(config?.courseId || "").match(COURSE_ID_PATTERN)?.[0];
    if (!config?.enabled || !label || !courseId) return null;
    const base = portalUrls?.studentPortal || process.env.STUDENT_PORTAL_ENDPOINT || "/portals/student";
    return { label, url: `${base}/course/detail/${courseId}` };
  }, [props?.courseButton, portalUrls]);

  const showCourseButton = isUserLoggedIn && roleLabel === "Student" && !!courseButton;

  // ── language / currency handlers ────────────────────────────────────────────
  const handleLanguageChange = async (newLang) => {
    if (newLang === languageCurrentValueRef.current) return false;
    const segments = parsePathSegments();
    if (LANGUAGES.includes(segments[0])) segments.shift();
    await changeLanguageByUser(newLang);
    return true;
  };

  const handleCurrencyChange = async (newCurrency) => {
    if (newCurrency === currencyCurrentValueRef.current) return;
    const user = AuthenticationService.instance().getCurrentUser();
    if (user) {
      user.currency = newCurrency;
      AuthenticationService.instance().setCurrentUser(user);
      await postChangeCurrency({ currency: newCurrency });
    } else {
      setCurrencyInLocalStorage(newCurrency, { explicit: true });
    }
  };

  // Apply-on-select handlers (used by LanguageCurrencyMenu)
  const onSelectLanguage = async (val) => {
    setSelectedLanguage(val);
    // changeLanguageByUser() already navigates to the new-language URL. Do NOT also
    // reload here — reloading the OLD url races that navigation, so the language
    // sometimes didn't change. (Matches HeaderAi/RegisterHeader, which only reload
    // when the language did NOT change.)
    await handleLanguageChange(val);
  };

  const onSelectCurrency = async (val) => {
    if (val === selectedCurrency) return;
    setSelectedCurrency(val);
    await handleCurrencyChange(val);
    // Apply the new currency site-wide (prices, cart totals) the same way a
    // language change does — otherwise the selection persists but nothing on the
    // page reflects it, so it looks like "currency didn't change".
    window.location.reload();
  };

  useClickOutside(langPopoverRef,      langMenuOpen,      setLangMenuOpen);
  useClickOutside(loginLangPopoverRef, loginLangMenuOpen, setLoginLangMenuOpen);
  useClickOutside(notifPopoverRef,     notifMenuOpen,     setNotifMenuOpen);
  useClickOutside(accountPopoverRef,   accountMenuOpen,   setAccountMenuOpen);

  // ── effects ─────────────────────────────────────────────────────────────────

  // Auth subscription
  useEffect(() => {
    const subscription = currentUser$.subscribe((user) => {
      setCurrentUser(user);
      setIsUserLoggedIn(!!user);
      if (user && user.userName === user.email) setUpdateProfileRequired(true);
    });
    return () => subscription.unsubscribe();
  }, [currentUser$]);

  // Enterprise check
  useEffect(() => {
    let isMounted = true;
    const fetchUserEnterprises = async () => {
      if (!isEnterprise || !isUserLoggedIn) {
        if (isMounted) setHasActiveEnterprise(false);
        return;
      }
      try {
        const enterprises = await getUserEnterprises();
        if (isMounted) setHasActiveEnterprise(Array.isArray(enterprises) && enterprises.length > 0);
      } catch {
        if (isMounted) setHasActiveEnterprise(false);
      }
    };
    fetchUserEnterprises();
    return () => { isMounted = false; };
  }, [isEnterprise, isUserLoggedIn]);

  // Init language from i18n
  useEffect(() => {
    if (!i18n.isInitialized) {
      initializeI18n().then(() => {
        languageCurrentValueRef.current = i18n.language;
        setSelectedLanguage(i18n.language);
      });
    } else {
      languageCurrentValueRef.current = i18n.language;
      setSelectedLanguage(i18n.language);
    }
  }, []);

  // Init currency
  useEffect(() => {
    const user = AuthenticationService.instance().getCurrentUser();
    if (user) {
      currencyCurrentValueRef.current = user.currency;
      setSelectedCurrency(user.currency);
    } else {
      const storedCurrency = getCurrencyFromLocalStorage();
      currencyCurrentValueRef.current = storedCurrency;
      setSelectedCurrency(storedCurrency);
    }
  }, []);

  // Translations
  useEffect(() => {
    if (!selectedLanguage) return;
    const loadTranslations = async () => {
      const t = {};
      t.homeTitle          = await translate("UMBRACO_LANDING.HEADER.MENU.HOME");
      t.coursesTitle       = await translate("UMBRACO_LANDING.HEADER.MENU.COURSES");
      t.trainersTitle      = await translate("UMBRACO_LANDING.HEADER.MENU.TRAINERS");
      t.aboutUsTitle       = await translate("UMBRACO_LANDING.HEADER.MENU.ABOUT_US");
      t.contactUsTitle     = await translate("UMBRACO_LANDING.HEADER.MENU.CONTACT_US");
      t.trainerRegistrationTitle = await translate("UMBRACO_LANDING.HEADER.MENU.TRAINER_REGISTRATION");
      t.findYourTrainerTitle     = await translate("UMBRACO_LANDING.HEADER.MENU.FIND_YOUR_TRAINER");
      t.realTimeLecturesTitle    = await translate("UMBRACO_LANDING.HEADER.MENU.1_1_COURSE");
      t.vietnamese  = await translate("LANGUAGES.VI");
      t.english     = await translate("LANGUAGES.EN");
      t.korean      = await translate("LANGUAGES.KO");
      t.myProfileTitle  = await translate("DASHBOARD_MENU_ITEMS.PROFILE");
      t.profileUpdateRequiredTitle       = await translate("DASHBOARD_MENU_ITEMS.PROFILE_UPDATE_REQUIRED");
      t.profileUpdateRequiredActionTitle = await translate("DASHBOARD_MENU_ITEMS.PROFILE_UPDATE_REQUIRED_ACTION");
      t.myScheduleTitle        = await translate("DASHBOARD_MENU_ITEMS.STUDENT_CALENDAR", false, "나의 일정");
      t.myTrainerScheduleTitle = await translate("DASHBOARD_MENU_ITEMS.TRAINER_CALENDAR", false, "강의 일정");
      t.myCoursesTitle         = await translate("DASHBOARD_MENU_ITEMS.MY_CLASS",         false, "나의 강의들");
      t.purchaseHistoryTitle   = await translate("DASHBOARD_MENU_ITEMS.PAYMENT",          false, "구매 내역");
      t.tuitionTitle           = await translate("DASHBOARD_MENU_ITEMS.TUITION",          false, "수업료");
      t.currencyUsdTitle = await translate("CURRENCY.MENU_ITEM_TITLE.USD",       false, "USD (미국 달러)");
      t.currencyKrwTitle = await translate("CURRENCY.MENU_ITEM_TITLE.KRW",       false, "KRW (한국원)");
      t.currencyVndTitle = await translate("CURRENCY.MENU_ITEM_TITLE.VND",       false, "VND (베트남 동)");
      t.currencyUsdShortTitle = await translate("CURRENCY.MENU_ITEM_TITLE.USD_SHORT", false, "달러");
      t.currencyKrwShortTitle = await translate("CURRENCY.MENU_ITEM_TITLE.KRW_SHORT", false, "원");
      t.currencyVndShortTitle = await translate("CURRENCY.MENU_ITEM_TITLE.VND_SHORT", false, "동");
      t.adminPortalTitle     = await translate("ACCOUNT.MENU_ITEM_TITLE.ADMIN_PORTAL",      false, "Go to Admin Portal");
      t.contentAdminTitle    = await translate("ACCOUNT.MENU_ITEM_TITLE.CONTENT_ADMIN",     false, "Go to Content Admin");
      t.trainerPortalTitle   = await translate("ACCOUNT.MENU_ITEM_TITLE.TRAINER_PORTAL",    false, "Go to Trainer Portal");
      t.studentPortalTitle   = await translate("ACCOUNT.MENU_ITEM_TITLE.STUDENT_PORTAL",    false, "Go to Student Portal");
      t.enterprisePortalTitle = await translate("ACCOUNT.MENU_ITEM_TITLE.ENTERPRISE_PORTAL", false, "Go to Enterprise Portal");
      t.logOutTitle          = await translate("ACCOUNT.MENU_ITEM_TITLE.LOG_OUT",            false, "Log out");
      t.goToHomepageTitle    = await translate("ACCOUNT.MENU_ITEM_TITLE.GO_TO_HOMEPAGE",     false, "Go to Homepage");
      t.becomeTutorTitle     = await translate("UMBRACO_LANDING.HEADER.BECOME_TUTOR",        false, "강사신청");
      t.languageTitle        = await translate("UMBRACO_LANDING.HEADER.LANGUAGE",            false, "언어");
      t.currencyTitle        = await translate("UMBRACO_LANDING.HEADER.CURRENCY",            false, "통화");
      t.saveButtonLabel      = await translate("UMBRACO_LANDING.HEADER.SAVE",                false, "저장");
      t.loginButtonLabel     = await translate("UMBRACO_LANDING.HEADER.LOG_IN",              false, "로그인");
      setTranslations(t);
    };
    loadTranslations();
  }, [selectedLanguage]);

  // Language options
  useEffect(() => {
    const fetchLanguageOptions = async () => {
      try {
        const options = await getLanguageOptions();
        const translatedOptions = await Promise.all(
          options.map(async (option) => ({
            ...option,
            name: await translate(option.name, true),
          }))
        );
        setLanguageOptions(translatedOptions);
      } catch (error) {
        console.error("Error fetching language options:", error);
      }
    };
    fetchLanguageOptions();
  }, []);

  // Currency options
  useEffect(() => {
    const fetchCurrencyOptions = async () => {
      try {
        const options = await getCurrencyOptions();
        const translatedOptions = await Promise.all(
          options.map(async (option) => {
            const [translatedName, translatedShortName] = await Promise.all([
              translate(option.name, true),
              translate(option.shortName, true),
            ]);
            return { ...option, name: translatedName, shortName: translatedShortName };
          })
        );
        setCurrencyOptions(translatedOptions);
      } catch (error) {
        console.error("Error fetching currency options:", error);
      }
    };
    fetchCurrencyOptions();
  }, []);

  // Account portal options (role-based)
  useEffect(() => {
    const options = [];
    if (isAdmin) {
      options.push({ name: translations?.adminPortalTitle, route: portalUrls?.adminPortal || "/portals/admin" });
    }
    if (isTrainer && !props?.tutorPortalDeactivated) {
      options.push({ name: translations?.trainerPortalTitle, route: portalUrls?.trainerPortal || "/portals/trainer" });
    }
    if (isStudent && !props?.studentPortalDeactivated) {
      options.push({ name: translations?.studentPortalTitle, route: portalUrls?.studentDashboard || "/portals/student/dashboard" });
    }
    if (isEnterprise && hasActiveEnterprise && !props?.enterprisePortalDeactivated) {
      options.push({ name: translations?.enterprisePortalTitle, route: portalUrls?.enterprisePortal || "/portals/enterprise" });
    }
    setAccountOptions(options);
  }, [props, translations, isAdmin, isTrainer, isStudent, isEnterprise, hasActiveEnterprise, portalUrls]);

  const profileDrawerNavItems = useMemo(() => {
    if (isStudent) {
      const base = portalUrls?.studentPortal || process.env.STUDENT_PORTAL_ENDPOINT || "/portals/student";
      return [
        ...(showCourseButton ? [{ route: courseButton.url, name: courseButton.label }] : []),
        { route: `${base}/timetable/`,       name: translations?.myScheduleTitle },
        { route: `${base}/course`,           name: translations?.myCoursesTitle },
        { route: `${base}/payment-history`,  name: translations?.purchaseHistoryTitle },
      ];
    }
    if (isTrainer) {
      const base = portalUrls?.trainerPortal || process.env.TRAINER_PORTAL_ENDPOINT || "/portals/trainer";
      return [
        { route: `${base}/calendar/`, name: translations?.myTrainerScheduleTitle },
        { route: `${base}/course`,    name: translations?.myCoursesTitle },
        { route: `${base}/tuition/`,  name: translations?.tuitionTitle },
      ];
    }
    return accountOptions;
  }, [isStudent, isTrainer, portalUrls, translations, accountOptions, showCourseButton, courseButton]);

  // Notifications: fetch + subscribe when logged in
  // Mirrors the portal NotificationBox pattern: ensure the SignalR connection is
  // initialised with the real API endpoint, then pull via GetAllNotifications so
  // the home GNB shows the same notifications as the admin/portal bell.
  useEffect(() => {
    if (!isUserLoggedIn || !currentUser?.id) return;

    const mapNotification = (n) => ({
      id:          n.id          ?? n.notificationId ?? String(n.createdTime ?? ""),
      title:       stripHtmlTags(n.title       ?? n.notificationTitle ?? ""),
      body:        stripHtmlTags(n.body        ?? n.message ?? n.content ?? ""),
      createdTime: n.createdTime ?? n.createdAt ?? n.created ?? null,
      isRead:      n.isRead       ?? (n.status === 1),
      // Kept for click->redirect (same fields the portal NotificationBox uses).
      pathUrl:     n.pathUrl      ?? "",
      receiver:    n.receiver     ?? "",
    });

    // GetAllNotifications emits { userNotifications, systemNotifications }; older
    // hub methods emit a flat array. Handle both.
    const sub = onNotificationsUpdated.subscribe((batch) => {
      if (!batch) return;
      const list = Array.isArray(batch)
        ? batch
        : [...(batch.userNotifications || []), ...(batch.systemNotifications || [])];
      setNotifications(list.map(mapNotification));
    });

    (async () => {
      try {
        if (!getHubConnection()) {
          await initializeConnection(process.env.API_ENDPOINT);
        }
        await getAllNotifications(currentUser.id, getI18nLanguageFromLocalStorage());
      } catch {
        /* connection/fetch failure — leave list empty */
      }
    })();

    return () => sub.unsubscribe();
  }, [isUserLoggedIn, currentUser?.id]);

  // Shopping cart: fetch + subscribe when logged in
  useEffect(() => {
    if (!isUserLoggedIn) return;

    const applyCart = (data) => {
      if (data) setCartCount((data.shoppingCartItems || []).length);
    };

    const sub = onShoppingCartUpdated.subscribe(applyCart);

    (async () => {
      try {
        await initializeShoppingCartConnection(process.env.API_ENDPOINT);
        await getShoppingCart();
        getShoppingCartHubConnection()?.on("ShoppingCartUpdated", applyCart);
      } catch {
        /* connection/fetch failure — leave count at 0 */
      }
    })();

    return () => sub.unsubscribe();
  }, [isUserLoggedIn]);

  useEffect(() => {
    if (!profileDrawerOpen) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [profileDrawerOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setProfileDrawerOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!profileDrawerOpen) return;
    const handleKeyDown = (e) => { if (e.key === "Escape") setProfileDrawerOpen(false); };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [profileDrawerOpen]);

  const handleOpenPopup = (popup) => {};

  const renderAvatarContent = () =>
    currentUser?.profilePicture ? (
      <img src={modifyMediaUrl(currentUser.profilePicture)} alt="" />
    ) : (
      <span className="gnb-avatar-initial">
        {(currentUser?.displayName || "?").trim().charAt(0).toUpperCase()}
      </span>
    );

  const handleAvatarClick = () => {
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches) {
      setProfileDrawerOpen(true);
    } else {
      setAccountMenuOpen((v) => !v);
    }
  };

  // ── Language trigger (shared across login / non-login) ───────────────────────
  const LangTrigger = ({ popoverRef, open, setOpen, loginVariant = false }) => (
    <div className="gnb-lang-trigger-wrap" ref={popoverRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="gnb-lang-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Flag code={getLanguageCode(languageCurrentValueRef.current)} style={{ width: 20 }} />
        <span className="gnb-lang-code">
          {(languageCurrentValueRef.current || selectedLanguage || "en").toUpperCase()}
        </span>
        <i className={`pi ${open ? "pi-angle-up" : "pi-angle-down"} gnb-lang-caret`} />
      </button>
      {open && (
        <div className="gnb-popover" style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 1200 }}>
          <LanguageCurrencyMenu
            languageOptions={languageOptions}
            currencyOptions={currencyOptions}
            selectedLanguage={selectedLanguage}
            selectedCurrency={selectedCurrency}
            onSelectLanguage={(val) => { setOpen(false); onSelectLanguage(val); }}
            onSelectCurrency={(val) => { onSelectCurrency(val); }}
          />
        </div>
      )}
    </div>
  );

  // ── Non-login right zone ─────────────────────────────────────────────────────
  const nonLoginActions = (
    <>
      <LangTrigger
        popoverRef={langPopoverRef}
        open={langMenuOpen}
        setOpen={setLangMenuOpen}
      />

      <a href={props?.loginUrl} className="gnb-pill--primary no-underline">
        <span className="gnb-pill-label">{translations?.loginButtonLabel}</span>
        <span className="gnb-pill-arrow" aria-hidden="true">→</span>
      </a>
    </>
  );

  // ── Login right zone ─────────────────────────────────────────────────────────
  const loginActions = (
    <>
      {showCourseButton && (
        <a href={courseButton.url} className="gnb-pill--primary gnb-course-btn no-underline">
          <span className="gnb-pill-label">{courseButton.label}</span>
        </a>
      )}

      <LangTrigger
        popoverRef={loginLangPopoverRef}
        open={loginLangMenuOpen}
        setOpen={setLoginLangMenuOpen}
        loginVariant
      />

      {/* Bell / Notifications */}
      <div className="gnb-icon-btn-wrap" ref={notifPopoverRef} style={{ position: "relative" }}>
        <button
          type="button"
          className="gnb-icon-btn"
          onClick={() => setNotifMenuOpen((v) => !v)}
          aria-label="Notifications"
        >
          <i className="pi pi-bell" />
          {unreadCount > 0 && <span className="gnb-badge" />}
        </button>
        {notifMenuOpen && (
          <div className="gnb-popover" style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 1200 }}>
            <NotificationMenu
              notifications={notifications}
              title="Notifications"
              emptyText="No new notifications"
              onItemClick={handleNotificationClick}
            />
          </div>
        )}
      </div>

      {/* Shopping cart */}
      <a
        href={`${languagePath}/shopping-cart`}
        className="gnb-icon-btn no-underline"
        aria-label="Shopping cart"
      >
        <i className="pi pi-shopping-bag" />
        {cartCount > 0 && <span className="gnb-badge" />}
      </a>

      {/* Avatar / Account */}
      <div className="gnb-avatar-wrap" ref={accountPopoverRef} style={{ position: "relative" }}>
        <button
          type="button"
          className="gnb-avatar"
          onClick={handleAvatarClick}
          aria-label="Account menu"
        >
          {renderAvatarContent()}
        </button>
        {accountMenuOpen && (
          <div className="gnb-popover" style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 1200 }}>
            <AccountMenu
              user={{
                displayName:    currentUser?.displayName    || "",
                profilePicture: currentUser?.profilePicture ? modifyMediaUrl(currentUser.profilePicture) : "",
              }}
              links={accountOptions}
              profileUrl={profileUrl}
              logoutUrl={props?.logoutUrl}
              roleLabel={roleLabel}
            />
          </div>
        )}
      </div>
    </>
  );

  const headerActions = isUserLoggedIn ? loginActions : nonLoginActions;

  return (
    <div>
      <div className="custom-menubar-placeholder gnb-global-placeholder"></div>
      <div className="keduall-header gnb-global custom-menubar fixed" ref={headerRef}>
        <MainNavigation
          language={languagePath}
          onOpenPopup={handleOpenPopup}
          rightSlot={headerActions}
          scheme={scheme}
        />
      </div>
      <Suspense fallback={<></>}>
        <AdvertiseModal />
      </Suspense>
      {isUserLoggedIn && profileDrawerOpen && (
        <div
          className="gnb-profile-drawer-overlay"
          onClick={() => setProfileDrawerOpen(false)}
        >
          <div className="gnb-profile-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="gnb-profile-drawer-bar">
              <div className="gnb-profile-drawer-bar-left">
                <button
                  type="button"
                  className="gnb-profile-drawer-close"
                  onClick={() => setProfileDrawerOpen(false)}
                  aria-label="Close"
                >
                  <i className="pi pi-times" />
                </button>
                <img
                  src={require("../icons/dotore-logo.svg")}
                  alt="DOTORE"
                  className="gnb-profile-drawer-logo"
                />
              </div>
              <span className="gnb-profile-drawer-bar-avatar" aria-hidden="true">
                {renderAvatarContent()}
              </span>
            </div>
            <div className="gnb-profile-drawer-body">
              <AccountMenu
                user={{
                  displayName:    currentUser?.displayName    || "",
                  profilePicture: currentUser?.profilePicture ? modifyMediaUrl(currentUser.profilePicture) : "",
                }}
                links={profileDrawerNavItems}
                profileUrl={profileUrl}
                logoutUrl={props?.logoutUrl}
                roleLabel={roleLabel}
                logoutLabel={translations?.logOutTitle}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
